import { useContext, useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import styled from '@emotion/styled';
import _ from 'lodash';
import { TPODataContext } from 'contexts/TPOContext';
import { PromoTactic, promoTacticIdNamePair } from 'types/tradePromoOptimization';
import { UserContext } from 'contexts/ConfigContext';
import { formatWord, getConfig, getDeleteButtonState, sendNotification } from 'utils/helperMethods';
import CustomTabs from 'components/atoms/CustomTabs/CustomTabs';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import PromoInfoSection from './PromoSections/PromoInfoSection';
import PromoMetricDetails from './PromoSections/PromoMetricDetails';
import { NEW_EVENT_PROMO_ID } from 'config/constants';
import axios from 'axios';
import ConfirmationModal from 'components/molecules/common/modals/ConfirmationModal';
import { antIconDanger } from '../PlanActions';
import dayjs from 'dayjs';
import { PromotionTypes } from 'utils/Enums';
import { setComputedValuePostPromoDetailsCall } from '../tpoHelperMethods';
import { queryClient } from 'services/reactQueryService';
import { TPO_QUERY_KEYS } from 'constants/tpo';

export const PromoSelect = styled(Select)`
    .ant-select-arrow-loading {
        color: blue !important;
        font-size: 16px;
    }
    .ant-select-selector {
        border-radius: 6px !important;
        border: none !important;
    }
    &.ant-select-multiple .ant-select-selection-item-remove {
        position: relative;
        top: -2px;
    }
`;

export enum MetricsApiTriggerPoints {
    None = 0,
    Date,
    Tactic,
    PPG,
}

type PromosProps = {
    onPromoActiveTabChange: (id: number) => void;
    mechanicType: string;
    eventDataLoading: boolean;
    disabled: boolean;
    eventId: null | string;
    isSavePending: boolean;
};
const Promos = ({
    onPromoActiveTabChange,
    mechanicType,
    eventDataLoading,
    disabled,
    eventId,
    isSavePending,
}: PromosProps) => {
    const {
        selectedEventInfo,
        updateSelectedEventProperty,
        promoActiveTab,
        isValid,
        setPromoActiveTab,
        setSelectedPromoPPGIds,
        promoConfigDictionary,
        isTpmTab,
        selectedPlanInfo,
        divisionMetricMapping,
        setHasCurrentPromoTacticInitialized,
        tempEventProperties,
    } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    const { promoLimit, deleteAllowedStatus = [] } = getConfig('tpo', appConfig);
    const divisionSpendMetrics = divisionMetricMapping[Number(selectedPlanInfo?.division)].spends;
    const divisionPromoMetrics = divisionMetricMapping[Number(selectedPlanInfo?.division)].metrics;
    const [fetchMetricsTriggeredFrom, setFetchMetricsTriggeredFrom] = useState<MetricsApiTriggerPoints>(
        MetricsApiTriggerPoints.None,
    );
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [deletePromoModalInfo, setDeletePromoModalInfo] = useState<{
        id?: string;
        name?: string;
        open?: boolean;
    }>({});

    //get promo details by promo id
    const { loading: activeTabPromoDetailsLoading } = useCommonApiHandler({
        method: 'get',
        queryKey: ['tpo_get_promo_details_by_id', promoActiveTab],
        queryConfig: {
            enabled: !!promoActiveTab && promoActiveTab !== NEW_EVENT_PROMO_ID && promoActiveTab !== '1',
            cacheTime: 0,
            staleTime: 0,
        }, // Disable caching
        url: `${tradePromoOptimizationUrls.getPromoDetailsByPromoId}/${promoActiveTab}`,
        parserFunction: (res: any) => {
            try {
                if (res) {
                    const avlMetrics = promoConfigDictionary?.promoMetricMapping[res.promotion_type_id]?.promoMetrics;
                    const avlSpendMetrics =
                        promoConfigDictionary?.promoMetricMapping[res.promotion_type_id]?.spendMetrics;
                    let updatedPPGs = res.ppgs.map((p: any) => {
                        return {
                            id: p?.lowest_dim_attr_id ?? null,
                            name: p?.lowest_dim_attr_name ?? '',
                            isSelected: true,
                            metrics: Array.isArray(p?.promo_metrics)
                                ? p.promo_metrics.map((m: any) => {
                                      return {
                                          id: m?.key ?? null,
                                          name: promoConfigDictionary.promoMetrics[m.key]?.name,
                                          value: m?.input_value ?? null,
                                          recomValue: m?.recom_value ?? null,
                                          isSelected: m?.is_selected ?? false,
                                          isHidden:
                                              !divisionPromoMetrics.includes(m?.key) ||
                                              !(
                                                  avlMetrics[m?.key]?.customerType === 'both' ||
                                                  avlMetrics[m?.key]?.customerType ===
                                                      selectedPlanInfo?.customerType.toLowerCase()
                                              ),
                                      };
                                  })
                                : [],
                            spendDetails: Array.isArray(p?.promo_spend_details)
                                ? p.promo_spend_details.map((m: any) => {
                                      return {
                                          id: m?.key ?? null,
                                          name: promoConfigDictionary.spendMetrics[m.key]?.name,
                                          value: m?.input_value ?? null,
                                          recomValue: m?.recom_value ?? null,
                                          isSelected: m?.is_selected ?? false,
                                          isHidden:
                                              !divisionSpendMetrics.includes(m?.key) ||
                                              !(
                                                  avlSpendMetrics[m?.key]?.customerType === 'both' ||
                                                  avlSpendMetrics[m?.key]?.customerType ===
                                                      selectedPlanInfo?.customerType.toLowerCase()
                                              ),
                                      };
                                  })
                                : [],
                        };
                    });

                    // GP_801 compute value using formulas
                    updatedPPGs = setComputedValuePostPromoDetailsCall(updatedPPGs, promoConfigDictionary);

                    const tempPromo: PromoTactic = {
                        name: res.name,
                        promoId: res.event_promo_id,
                        tpmPromoId: res.tpm_promo_id,
                        status: res.new_status?.trim(),
                        customPromo: res.custom_promo_grouping,
                        promoType: res.promotion_type_id,
                        tacticType: res.tactic_type_id,
                        featureQuality: res.tactic_feature_value,
                        displayQuality: res.tactic_display_value,
                        promoStartDate: res.promo_start_timestamp,
                        promoEndDate: res.promo_end_timestamp,
                        craEndDate: res.cra_end_timestamp,
                        craStartDate: res.cra_start_timestamp,
                        offerType: res.offer_type,
                        offer: res.offer_details.offer_mechanic,
                        manualOffer: res.offer_details.manual_details.type,
                        discount: res.offer_details.manual_details.value,
                        discountUnit: res.offer_details.manual_details.is_value_percentage ? '%' : 'abs',
                        buyGet:
                            res.offer_details.manual_details.buy && res.offer_details.manual_details.get
                                ? [res.offer_details.manual_details.buy, res.offer_details.manual_details.get]
                                : [0, 0],
                        comboUnit: res.offer_details.manual_details.combo_unit ?? 'abs',
                        buySaveUnit: res.offer_details.manual_details.bs_combo_unit ?? 'abs',
                        ppgs: updatedPPGs,
                        isLoading: false,
                        isRefreshRequired: false,
                        lastUpdatedAt: res.last_updated_at,
                        fundingRequest: res.funding_request,
                        approvedDate: res.approval_date,
                        contractNumber: res.contract,
                        parNumber: res.par,
                        ismCampaignId: res.ism_campaign_id,
                        buyFor:
                            res.offer_details.manual_details.bf_buy && res.offer_details.manual_details.bf_for
                                ? [res.offer_details.manual_details.bf_buy, res.offer_details.manual_details.bf_for]
                                : [0, 0],
                        buySave:
                            res.offer_details.manual_details.bs_buy && res.offer_details.manual_details.bs_save
                                ? [res.offer_details.manual_details.bs_buy, res.offer_details.manual_details.bs_save]
                                : [0, 0],
                        suggestedOffer: res?.promo_library_offer_details?.suggested_offer,
                        suggestedOfferType: res?.promo_library_offer_details?.sorting_order,
                        publishStatus: res?.publish_status,
                    };

                    updateSelectedEventProperty('currentPromoTactic', tempPromo);
                }
                if (res?.ppgs?.length > 0) {
                    setSelectedPromoPPGIds(res.ppgs.map((p: any) => p.lowest_dim_attr_id));
                }

                return {};
            } catch (error) {
                throw error;
            }
        },
    });

    useEffect(() => {
        setHasCurrentPromoTacticInitialized(
            !!selectedEventInfo?.currentPromoTactic?.promoId && !activeTabPromoDetailsLoading,
        );
    }, [selectedEventInfo?.currentPromoTactic?.promoId, activeTabPromoDetailsLoading]);

    //get promo details by promo id for first id
    const { loading: firstPromoDetailsLoading } = useCommonApiHandler({
        method: 'get',
        queryKey: ['first_tpo_get_promo_details_by_id', selectedEventInfo?.firstPromoTacticBackup?.promoId],
        queryConfig: {
            enabled:
                !!selectedEventInfo?.firstPromoTacticBackup?.promoId &&
                selectedEventInfo?.firstPromoTacticBackup?.promoId !== NEW_EVENT_PROMO_ID &&
                selectedEventInfo?.firstPromoTacticBackup?.promoId !== '1',
            cacheTime: 0,
            staleTime: 0,
        }, // Disable caching
        url: `${tradePromoOptimizationUrls.getPromoDetailsByPromoId}/${selectedEventInfo?.firstPromoTacticBackup?.promoId}`,
        parserFunction: (res: any) => {
            try {
                if (res) {
                    const avlMetrics = promoConfigDictionary?.promoMetricMapping[res.promotion_type_id]?.promoMetrics;
                    const avlSpendMetrics =
                        promoConfigDictionary?.promoMetricMapping[res.promotion_type_id]?.spendMetrics;
                    const updatedPPGs = res.ppgs.map((p: any) => {
                        return {
                            id: p?.lowest_dim_attr_id ?? null,
                            name: p?.lowest_dim_attr_name ?? '',
                            isSelected: true,
                            metrics: Array.isArray(p?.promo_metrics)
                                ? p.promo_metrics.map((m: any) => {
                                      return {
                                          id: m?.key ?? null,
                                          name: promoConfigDictionary.promoMetrics[m.key]?.name,
                                          value: m?.input_value ?? null,
                                          recomValue: m?.recom_value ?? null,
                                          isSelected: m?.is_selected ?? false,
                                          isHidden:
                                              !divisionPromoMetrics.includes(m?.key) ||
                                              !(
                                                  avlMetrics[m?.key]?.customerType === 'both' ||
                                                  avlMetrics[m?.key]?.customerType ===
                                                      selectedPlanInfo?.customerType.toLowerCase()
                                              ),
                                      };
                                  })
                                : [],
                            spendDetails: Array.isArray(p?.promo_spend_details)
                                ? p.promo_spend_details.map((m: any) => {
                                      return {
                                          id: m?.key ?? null,
                                          name: promoConfigDictionary.spendMetrics[m.key]?.name,
                                          value: m?.input_value ?? null,
                                          recomValue: m?.recom_value ?? null,
                                          isSelected: m?.is_selected ?? false,
                                          isHidden:
                                              !divisionSpendMetrics.includes(m?.key) ||
                                              !(
                                                  avlSpendMetrics[m?.key]?.customerType === 'both' ||
                                                  avlSpendMetrics[m?.key]?.customerType ===
                                                      selectedPlanInfo?.customerType.toLowerCase()
                                              ),
                                      };
                                  })
                                : [],
                        };
                    });

                    const tempPromo: PromoTactic = {
                        name: res.name,
                        promoId: res.event_promo_id,
                        tpmPromoId: res.tpm_promo_id,
                        status: res.new_status?.trim(),
                        customPromo: res.custom_promo_grouping,
                        promoType: res.promotion_type_id,
                        tacticType: res.tactic_type_id,
                        featureQuality: res.tactic_feature_value,
                        displayQuality: res.tactic_display_value,
                        promoStartDate: res.promo_start_timestamp,
                        promoEndDate: res.promo_end_timestamp,
                        craEndDate: res.cra_end_timestamp,
                        craStartDate: res.cra_start_timestamp,
                        offerType: res.offer_type,
                        offer: res.offer_details.offer_mechanic,
                        manualOffer: res.offer_details.manual_details.type,
                        discount: res.offer_details.manual_details.value,
                        discountUnit: res.offer_details.manual_details.is_value_percentage ? '%' : 'abs',
                        buyGet:
                            res.offer_details.manual_details.buy && res.offer_details.manual_details.get
                                ? [res.offer_details.manual_details.buy, res.offer_details.manual_details.get]
                                : [0, 0],
                        comboUnit: res.offer_details.manual_details.combo_unit ?? 'abs',
                        buySaveUnit: res.offer_details.manual_details.bs_combo_unit ?? 'abs',
                        ppgs: updatedPPGs,
                        isLoading: false,
                        fundingRequest: res.funding_request,
                        approvedDate: res.approved_on_date,
                        contractNumber: res.contract,
                        parNumber: res.par,
                        ismCampaignId: res.ism_campaign_id,
                        buyFor:
                            res.offer_details.manual_details.bf_buy && res.offer_details.manual_details.bf_for
                                ? [res.offer_details.manual_details.bf_buy, res.offer_details.manual_details.bf_for]
                                : [0, 0],
                        buySave:
                            res.offer_details.manual_details.bs_buy && res.offer_details.manual_details.bs_save
                                ? [res.offer_details.manual_details.bs_buy, res.offer_details.manual_details.bs_save]
                                : [0, 0],
                        suggestedOffer: res?.promo_library_offer_details?.suggested_offer,
                        suggestedOfferType: res?.promo_library_offer_details?.sorting_order,
                        publishStatus: res?.publish_status,
                    };

                    updateSelectedEventProperty('firstPromoTacticBackup', {
                        ...selectedEventInfo?.firstPromoTacticBackup,
                        promoInfo: tempPromo,
                    });
                }
                if (res?.ppgs?.length > 0) {
                    setSelectedPromoPPGIds(res.ppgs.map((p: any) => p.lowest_dim_attr_id));
                }

                return {};
            } catch (error) {
                throw error;
            }
        },
    });

    // TODO: disabling this feature for now
    // useLastUpdatedAtPolling(selectedEventInfo, updateSelectedEventProperty);

    const handlePromoDelete = (promoId: string, promoName: string) => {
        setDeletePromoModalInfo({ open: true, name: promoName, id: promoId });
    };

    const onPromoDelete = async () => {
        // check if promo has CRA end date in the past then throw an error notification and return
        // as per latest discussion we are removing this condition
        // if (
        //     selectedEventInfo?.currentPromoTactic?.craEndDate &&
        //     dayjs.unix(selectedEventInfo?.currentPromoTactic?.craEndDate).isBefore(dayjs().startOf('day'))
        // ) {
        //     sendNotification('error', 'Cannot delete promo with CRA end date in the past.');
        //     return;
        // }

        const { id: promoId } = deletePromoModalInfo;
        if (promoId === NEW_EVENT_PROMO_ID) {
            updatePostDel(promoId);
        } else {
            setDeleteLoading(true);
            await axios
                .delete(`${tradePromoOptimizationUrls.deletePromoUrl}/${promoId}`)
                .then(() => {
                    sendNotification('success', 'Promo deleted successfully');
                    updatePostDel(promoId as string);
                })
                .catch((err) => {
                    console.error(err);
                    sendNotification('error', 'Something went wrong. Unable to delete');
                })
                .finally(() => {
                    setDeleteLoading(false);
                });
        }
    };

    const updatePostDel = (promoId: string) => {
        const currentTabs = selectedEventInfo?.promoTacticsIdNamePair;
        const newTabs = currentTabs?.filter((tab) => tab.id !== promoId);
        if (newTabs?.length) {
            updateSelectedEventProperty('promoTacticsIdNamePair', newTabs);
            setPromoActiveTab(newTabs[newTabs?.length - 1].id);
        } else {
            updateSelectedEventProperty('promoTacticsIdNamePair', undefined);
            setPromoActiveTab(null);
        }
        setDeletePromoModalInfo({});
    };

    const onAddPromoBtnClick = () => {
        queryClient.invalidateQueries({
            queryKey: [TPO_QUERY_KEYS.GET_METRICS_RECOMMENDED_VALUES],
            exact: false,
        });

        const currentPromos = selectedEventInfo?.promoTacticsIdNamePair || [];

        // 1️⃣ Enforce max promo limit
        if (currentPromos.length >= promoLimit) {
            sendNotification('error', `Upto ${promoLimit} promos can be added`);
            return;
        }

        // 2️⃣ Append new promo to local copy
        const updatedPromos = [...currentPromos, { id: NEW_EVENT_PROMO_ID, name: '', promotionType: null }];

        updateSelectedEventProperty('promoTacticsIdNamePair', updatedPromos);

        // 3️⃣ Prepare promo tactic config
        let newPromoTactic;

        const isFirstPromo = currentPromos.length === 0;

        if (isFirstPromo) {
            newPromoTactic = {
                name: '',
                promoId: NEW_EVENT_PROMO_ID,
                tpmPromoId: null,
                status: 'planned',
                customPromo: null,
                promoType: null,
                tacticType: null,
                promoStartDate: null,
                promoEndDate: null,
                ppgs: [],
                featureQuality: null,
                displayQuality: null,
                offerType: null,
                manualOffer: null,
                isLoading: false,
                buyGet: [0, 0],
                buyFor: [0, 0],
                discount: null,
                offer: null,
                craEndDate: null,
                craStartDate: null,
                comboUnit: 'abs',
                discountUnit: 'abs',
                buySaveUnit: 'abs',
                lastUpdatedAt: Date.now(),
            };

            setSelectedPromoPPGIds([]);
        } else if (selectedEventInfo?.firstPromoTacticBackup?.promoInfo) {
            newPromoTactic = _.cloneDeep(selectedEventInfo.firstPromoTacticBackup.promoInfo);
            const startDate = newPromoTactic.promoStartDate;

            newPromoTactic.name = '';
            newPromoTactic.promoId = NEW_EVENT_PROMO_ID;
            newPromoTactic.hasChanges = true;
            newPromoTactic.status = 'planned';
            newPromoTactic.tpmPromoId = null;

            if (startDate && dayjs.unix(startDate).isBefore(dayjs().startOf('day'))) {
                newPromoTactic.promoStartDate = null;
                newPromoTactic.promoEndDate = null;
                newPromoTactic.craStartDate = null;
                newPromoTactic.craEndDate = null;
                newPromoTactic.ppgs = [];

                setSelectedPromoPPGIds([]);

                sendNotification(
                    'info',
                    'The original promo is in the past. Basic details copied, but dates have been reset. Please configure new dates.',
                );
            } else {
                sendNotification('info', 'Copied from the first Promo');
            }
        }

        // 4️⃣ Set the tactic (if created above)
        if (newPromoTactic) {
            updateSelectedEventProperty('currentPromoTactic', newPromoTactic as PromoTactic);
        }

        // 5️⃣ Set UI tab state
        onPromoActiveTabChange(0);
        setPromoActiveTab(NEW_EVENT_PROMO_ID);
    };

    // show add button only if there is no edlp promo
    const showAddButton = useMemo(() => {
        return !selectedEventInfo?.promoTacticsIdNamePair?.some(
            (p: promoTacticIdNamePair) => p.promotionType === PromotionTypes.EDLP,
        );
    }, [selectedEventInfo?.promoTacticsIdNamePair]);

    const promoTacticsIdNamePairLength = selectedEventInfo?.promoTacticsIdNamePair?.length || 0;

    // Get delete button state and tooltip
    const deleteButtonState = useMemo(() => {
        const status = selectedEventInfo?.currentPromoTactic?.status?.toLowerCase();
        const craStartDate = tempEventProperties?.CRAStartDate;
        const is_ever_successfully_published = selectedEventInfo?.is_ever_successfully_published || false;
        const promoType = selectedEventInfo?.currentPromoTactic?.promoType;

        return getDeleteButtonState(
            status,
            craStartDate,
            deleteAllowedStatus,
            is_ever_successfully_published,
            promoType,
        );
    }, [
        selectedEventInfo?.currentPromoTactic?.status,
        selectedEventInfo?.is_ever_successfully_published,
        selectedEventInfo?.currentPromoTactic?.promoType,
        tempEventProperties?.CRAStartDate,
        deleteAllowedStatus,
    ]);

    return (
        <>
            <CustomTabs
                tabs={
                    selectedEventInfo?.promoTacticsIdNamePair?.map((p: promoTacticIdNamePair) => ({
                        key: p.id,
                        label: p.name,
                    })) || []
                }
                showLoader={
                    promoActiveTab !== NEW_EVENT_PROMO_ID &&
                    promoTacticsIdNamePairLength != 0 &&
                    firstPromoDetailsLoading
                }
                activeTab={promoActiveTab}
                setActiveTab={onPromoActiveTabChange}
                showAddButton={showAddButton}
                isViewMode={disabled || isTpmTab}
                onAddBtnClick={onAddPromoBtnClick}
                // todo remove the condition
                isAddDisabled={
                    promoTacticsIdNamePairLength !== 0 &&
                    ((!!selectedEventInfo?.promoTacticsIdNamePair?.length && !isValid) || // if current form is invalid
                        !!selectedEventInfo?.currentPromoTactic?.hasChanges || // if current form has changes then disable it
                        firstPromoDetailsLoading) // we need first promo details to be loaded before copying it to new promo
                }
                handleTabDelete={handlePromoDelete}
                isDeleteDisabled={deleteButtonState.isDisabled}
                deleteBtnDisabledTooltip={deleteButtonState.tooltip}
            />
            {!selectedEventInfo?.promoTacticsIdNamePair?.length ? (
                <div className=" flex-1 border border-backgroundGreyAlt6  bg-backgroundGreyAlt6  rounded p-3   flex justify-center items-center  ">
                    Please click on Add Promo button to add promo.
                </div>
            ) : (
                <div className="flex-1 overflow-hidden">
                    {activeTabPromoDetailsLoading || eventDataLoading ? (
                        <div className="h-full border border-backgroundGreyAlt6  bg-backgroundGreyAlt6  rounded p-3  flex  justify-center items-center  ">
                            <Spin />
                        </div>
                    ) : (
                        <div className="h-full overflow-hidden  ">
                            {/* PROMO COMPONENT */}
                            <div className="h-full flex flex-col  border border-backgroundGreyAlt6  rounded p-3  bg-white gap-2">
                                <PromoInfoSection
                                    fetchMetricsTriggeredFrom={fetchMetricsTriggeredFrom}
                                    setFetchMetricsTriggeredFrom={setFetchMetricsTriggeredFrom}
                                    mechanicType={mechanicType}
                                    eventId={eventId}
                                    isSavePending={isSavePending}
                                />
                                <PromoMetricDetails
                                    setFetchMetricsTriggeredFrom={setFetchMetricsTriggeredFrom}
                                    mechanicType={mechanicType}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
            <ConfirmationModal
                open={!!deletePromoModalInfo?.open}
                onCancel={() => {
                    setDeletePromoModalInfo((prev) => ({ ...prev, open: false }));
                }}
                onSubmitClick={onPromoDelete}
                okText={deleteLoading ? <Spin indicator={antIconDanger} /> : 'Delete'}
                okButtonProps={{ type: 'ghost', danger: true, style: { borderRadius: 6 } }}
                loading={deleteLoading}
                subTitle={`Are you sure you want to delete ${deletePromoModalInfo?.name}?`}
                title="Delete Promo"
                description={`This will delete ${deletePromoModalInfo?.name} from the event`}
            />
        </>
    );
};

export default Promos;
